/**
 * ProvenanceChip — the discriminated-union renderer (PR-C1 §6).
 *
 * ONE component takes a `Provenance | DerivedProvenance` and renders the
 * right label per arm. Design review (2026-07-06): provenance is QUIET
 * MUTED TEXT, not a chip — small ink-soft prose with a prose date, and the
 * link signalled by the trailing arrow. Founder polish (same day): dates
 * read RELATIVE for the freshest two days ("from your note yesterday ↗")
 * and absolute prose beyond that ("from your note on July 5, 2026 ↗") —
 * `today` comes from TodayContext (page-injected; never Date.now() here):
 *   - note        → "from your note {when} ↗" as a LINK to the Notes tab,
 *                    anchored on the note (?tab=notes#note-{noteId}).
 *   - direct_edit → "edited {when}" (no link).
 *   - intake_seed → "from intake".
 *   - null        → renders nothing (housing only — a derived projection
 *                    with no recorded source event).
 *
 * Tokens only (ink-soft / sage). No chip background — confirmed provenance
 * is not a status; it reads in words.
 */

import { type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { DerivedProvenance, Provenance } from '@/lib/clientRecord';
import { proseWhen } from '@/lib/proseDate';
import { useToday } from '@/lib/today';

export interface ProvenanceChipProps {
  provenance: Provenance | DerivedProvenance;
}

// Quiet muted TEXT, not a chip: no background, small ink-soft prose.
const CHIP_BASE = 'inline-flex items-center gap-1 text-[11px] text-ink-soft';

export function ProvenanceChip({ provenance }: ProvenanceChipProps): ReactNode {
  const { tenantSlug, clientId } = useParams<{ tenantSlug: string; clientId: string }>();
  // Page-injected local day (undefined outside a provider → absolute dates).
  const today = useToday();

  // Housing's derived provenance may be null — render nothing.
  if (provenance === null) return null;

  if (provenance.kind === 'note') {
    // Link to the Notes tab, anchored on the note. Design Pass 2 (A4,
    // provenance demotion): the receipt reads as QUIET METADATA — the
    // same muted ink as the other arms, waking to link color/underline
    // only on hover or focus. Link weight belongs to actions; the note
    // stays exactly one click away, as before.
    const to = `/t/${tenantSlug ?? ''}/clients/${clientId ?? ''}?tab=notes#note-${provenance.noteId}`;
    return (
      <Link
        to={to}
        data-testid="provenance-chip"
        data-kind="note"
        className={`${CHIP_BASE} hover:text-sage-700 hover:underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 rounded-tight`}
      >
        from your note {proseWhen(provenance.noteDate, today)}
        <span aria-hidden="true" className="opacity-60">↗</span>
      </Link>
    );
  }

  if (provenance.kind === 'direct_edit') {
    return (
      <span data-testid="provenance-chip" data-kind="direct_edit" className={CHIP_BASE}>
        edited {proseWhen(provenance.editedAt, today)}
      </span>
    );
  }

  // intake_seed
  return (
    <span data-testid="provenance-chip" data-kind="intake_seed" className={CHIP_BASE}>
      from intake
    </span>
  );
}
